import { useImageReady } from './useImageReady';

export interface BackdropProps {
  /** Photo URL for the upper portion of the page; null hides the photo layer. */
  bgImageUrl: string | null;
  /**
   * `'cover'` crops the image to fill viewport (modern Pack default).
   * `'auto'` preserves intrinsic image scale, centred (classic Pack and
   * JustPulledPage's choice — keeps stadium/lawn art at native scale).
   * Defaults to `'cover'`.
   */
  fit?: 'cover' | 'auto';
  /**
   * Fill the top inset strip with a copy of the background, so content
   * scrolled past it disappears behind the notch instead of showing through.
   *
   * Only for a page whose header does NOT bleed into that strip. With a
   * bleeding header (`header-safe-top`) this cover would sit between the
   * glass and the scrolling content, and the bar would blur the copy instead
   * of the content — so the modern Pack page, My Packs, Shipping, Delivery
   * and Just Pulled all leave it off. The classic Pack page needs it: its
   * mobile Just Pulled strip sits above the header, which therefore parks
   * below the inset and leaves the band uncovered.
   */
  topInsetCover?: boolean;
}

const PHOTO_MASK = 'linear-gradient(180deg, black 0%, black 60%, transparent 100%)';

export function Backdrop({
  bgImageUrl,
  fit = 'cover',
  topInsetCover = false,
}: BackdropProps) {
  const ready = useImageReady(bgImageUrl);
  // Layer styles are shared with the safe-area cover below, so the strip
  // paints the SAME pixels as the backdrop it continues — a hand-picked
  // strip colour would drift from the gradient, and would miss the photo
  // entirely on packs that have one.
  const gradientStyle = { backgroundImage: 'var(--ui-bg-gradient)' };
  const photoStyle = bgImageUrl
    ? {
        height: '100vh',
        opacity: ready ? 1 : 0,
        backgroundImage: `url('${bgImageUrl}')`,
        backgroundSize: fit,
        backgroundPosition: 'center center',
        backgroundRepeat: 'no-repeat',
        maskImage: PHOTO_MASK,
        WebkitMaskImage: PHOTO_MASK,
      }
    : null;

  return (
    <>
      <div
        aria-hidden
        data-testid="backdrop-gradient"
        className="pointer-events-none fixed inset-0 -z-20"
        style={gradientStyle}
      />
      {photoStyle && (
        <div
          aria-hidden
          data-testid="backdrop-photo"
          data-ready={ready ? 'true' : 'false'}
          className="pointer-events-none fixed left-0 right-0 top-0 -z-10 transition-opacity duration-700 ease-out"
          style={photoStyle}
        />
      )}

      {/* Safe-area cover — fills the top inset strip ABOVE the content, so
          scrolled content disappears behind the notch instead of showing
          through it. Opt-in (`topInsetCover`), because a page whose header
          bleeds into the strip must NOT have one; see the prop's doc comment.
          Zero-height, and therefore invisible, until a top inset is set.

          The mechanics (`contain: paint`, viewport-anchored copies, z-40) are
          the bottom strip's, described below. */}
      {topInsetCover && (
        <div
          aria-hidden
          data-testid="backdrop-safe-area"
          className="pointer-events-none fixed left-0 right-0 top-0 z-40 [contain:paint]"
          style={{ height: 'var(--page-safe-top)' }}
        >
          <div className="fixed left-0 top-0 h-screen w-screen" style={gradientStyle} />
          {photoStyle && (
            <div
              className="fixed left-0 top-0 w-screen transition-opacity duration-700 ease-out"
              style={photoStyle}
            />
          )}
        </div>
      )}

      {/* Safe-area cover — bottom twin of the strip above, for hosts that draw
          their own navigation / home indicator over the frame's bottom edge
          (`PageLayout.bottomInsetCover` = `none` opts out). Zero-height, and
          therefore invisible, until a bottom inset is set. Unlike the top one
          this is unconditional: no bar bleeds into the bottom strip.

          `contain: paint` makes this strip the containing block for its
          fixed children AND clips them to it — that's what lets the copies
          keep viewport geometry (`w-screen h-screen`) while only the bottom
          band of it paints. `inset-0` on the copies would size the gradient
          to the strip and land a different slice of it.

          The copies anchor to `bottom-0` (not `top-0`): inside this
          `contain: paint` box the strip's own bottom edge coincides with the
          viewport bottom, so a bottom-anchored `h-screen` copy lands exactly
          on the viewport rect the real layers use.

          z-40 deliberately sits under the DS Modal (z-50): a dialog is
          allowed to cover the strip, page content is not. Fixed bottom BARS
          (pack rail, My Packs toolbar) are not page content either — they fill
          the strip with their own surface on purpose, so they ride above this
          at z-45; anything new that does the same has to clear z-40 too. */}
      <div
        aria-hidden
        data-testid="backdrop-safe-area-bottom"
        className="pointer-events-none fixed bottom-0 left-0 right-0 z-40 [contain:paint]"
        style={{
          display: 'var(--co-PageLayout-bottomInsetCover, block)',
          height: 'var(--page-safe-bottom)',
        }}
      >
        <div className="fixed bottom-0 left-0 h-screen w-screen" style={gradientStyle} />
        {photoStyle && (
          <div
            className="fixed bottom-0 left-0 w-screen transition-opacity duration-700 ease-out"
            style={photoStyle}
          />
        )}
      </div>
    </>
  );
}
